import { useState, useEffect } from 'react';
import { DealFilterOptions } from '@/types';
import { watchlistApi } from '@/lib/api/watchlistApi';

// Helper function to get dynamic dates
const getDynamicDates = () => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  
  return {
    time_period_start: oneMonthAgo.toISOString().split('T')[0],
    time_period_end: today.toISOString().split('T')[0]
  };
};

// Hardcoded filter options - no backend API call for basic filters
const getBaseFilterOptions = (): Omit<DealFilterOptions, 'watchlists'> => {
  const dates = getDynamicDates();
  
  return {
    deal_type: ["Block", "Bulk", "Insider", "SAST"],
    market_cap_min: 0,
    market_cap_max: 1000000000000,
    sme: [true, false],
    time_period_start: dates.time_period_start,
    time_period_end: dates.time_period_end,
    esm_stage: ["Yes", "No"],
    asm_stage: ["Yes", "No"],
    gsm_status: ["Yes", "No"],
    rights: ["A", "B", "C", "H", "X", "Z"],
    open_offer: ["Active", "Close"],
    pref: ["Active"],
    smart_investor: [true, false]
  };
};

export function useDealFilterOptions() {
  const [filterOptions, setFilterOptions] = useState<DealFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get base options
        const baseOptions = getBaseFilterOptions();
        
        // Fetch watchlists
        const watchlistsResponse = await watchlistApi.listWatchlists();
        const watchlists = watchlistsResponse.watchlists.map(w => ({
          id: w.id,
          name: w.name,
          number_of_stocks: (w as typeof w & { number_of_stocks?: number }).number_of_stocks || 0,
          watchlist_type: (w as typeof w & { watchlist_type?: 'default' | 'system_generated' }).watchlist_type || 'default'
        }));

        // Combine base options with watchlists
        const updatedOptions: DealFilterOptions = {
          ...baseOptions,
          watchlists
        };

        setFilterOptions(updatedOptions);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        // Fallback to base options without watchlists
        setFilterOptions({
          ...getBaseFilterOptions(),
          watchlists: []
        });
        setError('Failed to load watchlists, but other filters are available');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  return {
    filterOptions,
    loading,
    error,
  };
}

// Export base options for use in other components (backwards compatibility)
export { getBaseFilterOptions as getHardcodedFilterOptions }; 